// The one place a public page's search metadata lives.
//
// Three things read this file and must agree, which is why it is data and not
// JSX scattered across route files:
//
//   1. scripts/prerender.mjs writes one HTML file per page at build time, with
//      this page's <title>, description, canonical and JSON-LD already in the
//      <head>. Crawlers that do not run JavaScript (most AI crawlers, and Bing
//      on a bad day) read that file and nothing else.
//   2. useSeo() applies the same tags in the browser on client-side navigation,
//      so a rendering crawler and a person's tab see the same thing.
//   3. sitemap.xml and the Cloudflare Pages _redirects file are generated from
//      the lists below, so a page cannot be indexable and unreachable, or in
//      the sitemap and noindex.
//
// Until 2026-10-07 every URL served the same index.html: one title, one
// description, and a canonical hard-coded to "/" that told Google /pricing and
// /how-it-works were duplicates of the homepage.
//
// No React and no DOM in here: the prerender script and the tests import it
// under plain Node.

import { PLAN_CREDIT_POLICIES } from "@mockingbird/engine/quota";
import { PAGE_FAQ, type Faq } from "./faq";

export { LANDING_FAQ } from "./faq";

export const SITE_ORIGIN = "https://mockingbird.digital";
export const SITE_NAME = "Mockingbird";
export const PUBLISHER_NAME = "Onwards Analytics";
export const PUBLISHER_URL = "https://onwardsanalytics.com.au";
/** The 1200 x 630 card networks show when a link is shared. Source and how to
 *  re-render it: apps/web/scripts/og-image/. */
export const DEFAULT_OG_IMAGE = `${SITE_ORIGIN}/og/mockingbird-share.jpg`;
export const DEFAULT_OG_IMAGE_ALT =
  "Mockingbird: a competitor's ad next to the same ad rebuilt for another brand.";


const TRIAL_PRICE_USD = PLAN_CREDIT_POLICIES.trial.monthlyPriceUsd;
const BASIC_PRICE_USD = PLAN_CREDIT_POLICIES.basic.monthlyPriceUsd;
const PRO_PRICE_USD = PLAN_CREDIT_POLICIES.pro.monthlyPriceUsd;

export interface SeoPage {
  /** Router path, no trailing slash (except "/"). Also the canonical path. */
  path: string;
  /** <title>. Aim for 50-60 characters; the brand goes in every one. */
  title: string;
  /** Meta description. Aim for 140-160 characters. */
  description: string;
  /** False adds noindex. A noindex page is never written to the sitemap. */
  indexable: boolean;
  /** Present = listed in sitemap.xml. */
  sitemap?: { priority: number; changefreq: "daily" | "weekly" | "monthly" | "yearly" };
  /** ISO date of the last real content change. Hand-maintained on purpose:
   *  a lastmod that moves on every deploy teaches Google to ignore it. */
  updated: string;
  /** ISO date a dated piece (a blog post) first went up. Becomes datePublished. */
  published?: string;
  /** Short label for breadcrumbs. */
  breadcrumb: string;
  /** Path of the page one level up, when that is not the homepage. */
  parent?: string;
  /** "article" for long-form pages; everything else is "website". */
  ogType?: "website" | "article";
  /**
   * True writes the page's own <head> but leaves #root empty. For pages that
   * are a form and nothing else (signup, login): there is no content to show
   * a crawler, and both read window.location while rendering.
   */
  headOnly?: boolean;
}

/**
 * Pages written to static HTML at build time. Order is sitemap order.
 */
export const PUBLIC_PAGES: readonly SeoPage[] = [
  {
    path: "/",
    title: "Mockingbird | AI Meta Ad Generator & Competitor Ad Research",
    description:
      "Mockingbird watches your competitors in the Meta Ad Library, scores their best ads, and rebuilds the winners for your brand, ready to upload to Meta Ads Manager.",
    indexable: true,
    sitemap: { priority: 1.0, changefreq: "weekly" },
    updated: "2026-10-07",
    breadcrumb: "Home",
  },
  {
    path: "/how-it-works",
    title: "How Mockingbird Works | Competitor Ad to Your Meta Ad",
    description:
      "See how Mockingbird turns a competitor's Meta ad into a test for your brand: a daily competitor scan, scoring, brand-matched generation, and export sized for Meta.",
    indexable: true,
    sitemap: { priority: 0.8, changefreq: "monthly" },
    updated: "2026-10-07",
    breadcrumb: "How it works",
  },
  {
    path: "/pricing",
    title: `Mockingbird Pricing | Plans from $${BASIC_PRICE_USD}/mo, $${TRIAL_PRICE_USD} Trial`,
    description: `Mockingbird plans start at $${BASIC_PRICE_USD} a month, with Pro at $${PRO_PRICE_USD}. Try it for 7 days for $${TRIAL_PRICE_USD}: daily competitor ad drops and on-brand ad generation included.`,
    indexable: true,
    sitemap: { priority: 0.8, changefreq: "monthly" },
    updated: "2026-10-07",
    breadcrumb: "Pricing",
  },
  {
    path: "/competitor-ad-research",
    title: "Competitor Ad Research for Meta Ads | Mockingbird",
    description:
      "Find your competitors' Facebook and Instagram ads, see which ones they keep running, and get the strongest sent to you daily. Paste a Page to start.",
    indexable: true,
    sitemap: { priority: 0.9, changefreq: "monthly" },
    updated: "2026-10-07",
    breadcrumb: "Competitor ad research",
  },
  {
    path: "/ai-ad-generator",
    title: "AI Ad Generator for Meta Ads, Built on Real Ads | Mockingbird",
    description:
      "An AI ad generator that starts from a competitor's proven Meta ad, keeps the layout and hook, and rebuilds it in your brand's colours, voice and product.",
    indexable: true,
    sitemap: { priority: 0.9, changefreq: "monthly" },
    updated: "2026-10-07",
    breadcrumb: "AI ad generator",
  },
  {
    path: "/meta-ad-library-alternative",
    title: "Meta (Facebook) Ad Library Alternative | Mockingbird",
    description:
      "The Meta Ad Library shows you ads. Mockingbird watches your competitors in it every day, ranks what it finds, and rebuilds the best for your brand.",
    indexable: true,
    sitemap: { priority: 0.8, changefreq: "monthly" },
    updated: "2026-10-13",
    breadcrumb: "Meta Ad Library alternative",
  },
  {
    path: "/examples",
    title: "Facebook Ad Examples: 20 Competitor Ads Rebuilt | Mockingbird",
    description:
      "Twenty real Facebook ad creative examples from the Meta Ad Library, each next to the ad Mockingbird rebuilt from it. Same layout and hook, new brand.",
    indexable: true,
    sitemap: { priority: 0.8, changefreq: "monthly" },
    updated: "2026-10-13",
    breadcrumb: "Ad examples",
  },
  {
    path: "/foreplay-alternative",
    title: "Foreplay Alternative for Meta Advertisers | Mockingbird",
    description:
      "Foreplay saves, tracks and briefs. Mockingbird tracks your competitors on Meta and rebuilds their best ad as a finished ad in your brand. A fair comparison.",
    indexable: true,
    sitemap: { priority: 0.7, changefreq: "monthly" },
    updated: "2026-10-07",
    breadcrumb: "Foreplay alternative",
  },
  {
    path: "/adspy-alternative",
    title: "AdSpy Alternative: Track Competitors Daily | Mockingbird",
    description:
      "AdSpy searches a database of Facebook and Instagram ads. Mockingbird follows the competitors you name, ranks their new ads daily and rebuilds them for you.",
    indexable: true,
    sitemap: { priority: 0.7, changefreq: "monthly" },
    updated: "2026-10-07",
    breadcrumb: "AdSpy alternative",
  },
  {
    path: "/adcreative-ai-alternative",
    title: "AdCreative.ai Alternative Built on Real Ads | Mockingbird",
    description:
      "AdCreative.ai generates many kinds of ad creative. Mockingbird starts each ad from a competitor's running Meta ad and rebuilds it in your brand. Compare the two.",
    indexable: true,
    sitemap: { priority: 0.7, changefreq: "monthly" },
    updated: "2026-10-07",
    breadcrumb: "AdCreative.ai alternative",
  },
  {
    path: "/gethookd-alternative",
    title: "GetHookd Alternative for Daily Competitor Tracking | Mockingbird",
    description:
      "GetHookd is a searchable Meta ad library with cloning. Mockingbird follows the competitors you name, ranks their new ads daily and rebuilds them. Compare the two.",
    indexable: true,
    sitemap: { priority: 0.7, changefreq: "monthly" },
    updated: "2026-10-09",
    breadcrumb: "GetHookd alternative",
  },
  {
    path: "/adstellar-alternative",
    title: "AdStellar Alternative for Meta Image Ads | Mockingbird",
    description:
      "AdStellar creates, launches and optimises Meta campaigns. Mockingbird tracks your competitors daily and rebuilds their best image ad in your brand. Compare the two.",
    indexable: true,
    sitemap: { priority: 0.7, changefreq: "monthly" },
    updated: "2026-10-09",
    breadcrumb: "AdStellar alternative",
  },
  {
    path: "/atria-alternative",
    title: "Atria Alternative for Small Meta Ad Teams | Mockingbird",
    description:
      "Atria manages the creative workflow across Meta and TikTok. Mockingbird tracks your competitors on Meta and rebuilds their best image ad in your brand. Compare.",
    indexable: true,
    sitemap: { priority: 0.7, changefreq: "monthly" },
    updated: "2026-10-09",
    breadcrumb: "Atria alternative",
  },
  {
    path: "/guides",
    title: "Meta Ads Guides: Ad Library, Publishing, Ranking | Mockingbird",
    description:
      "Plain-language guides to Meta advertising: how to search the Meta Ad Library, how to publish an ad in Ads Manager, and how Meta ranks ads in its auction.",
    indexable: true,
    sitemap: { priority: 0.7, changefreq: "weekly" },
    updated: "2026-10-07",
    breadcrumb: "Guides",
  },
  {
    path: "/guides/meta-ad-library",
    title: "Meta Ad Library: How to Search Competitor Ads | Mockingbird",
    description:
      "How to use the Meta Ad Library (Facebook Ad Library) to search ads by advertiser or keyword, what each listing tells you, and what it leaves out.",
    indexable: true,
    sitemap: { priority: 0.8, changefreq: "monthly" },
    updated: "2026-10-13",
    breadcrumb: "Meta Ad Library guide",
    parent: "/guides",
    ogType: "article",
  },
  {
    path: "/guides/how-to-publish-meta-ads",
    title: "How to Publish Ads on Meta: Step by Step | Mockingbird",
    description:
      "A step-by-step guide to publishing an ad on Facebook and Instagram in Meta Ads Manager: what you need first, campaign, ad set, ad, review and launch.",
    indexable: true,
    sitemap: { priority: 0.8, changefreq: "monthly" },
    updated: "2026-10-07",
    breadcrumb: "How to publish Meta ads",
    parent: "/guides",
    ogType: "article",
  },
  {
    path: "/guides/meta-ad-ranking",
    title: "How Meta Ranks Ads and How to Rank Higher | Mockingbird",
    description:
      "How the Meta ad auction decides which ad is shown, what quality, engagement and conversion rankings mean, and what to change when an ad ranks below average.",
    indexable: true,
    sitemap: { priority: 0.8, changefreq: "monthly" },
    updated: "2026-10-07",
    breadcrumb: "How Meta ranks ads",
    parent: "/guides",
    ogType: "article",
  },
  {
    path: "/guides/find-competitor-ads-instagram",
    title: "How to Find Competitor Ads on Instagram | Mockingbird",
    description:
      "See any brand's Instagram ads for free: search the Meta Ad Library, filter the platform to Instagram, or use the Active ads link on their profile.",
    indexable: true,
    sitemap: { priority: 0.8, changefreq: "monthly" },
    updated: "2026-10-07",
    breadcrumb: "Find competitor ads on Instagram",
    parent: "/guides",
    ogType: "article",
  },
  {
    path: "/guides/spy-on-competitor-ads",
    title: "How to Spy on Competitor Ads Legally (Free) | Mockingbird",
    description:
      "The free public ad libraries on Meta, Google, TikTok and LinkedIn, what each one shows, and where the legal line sits between research and copying.",
    indexable: true,
    sitemap: { priority: 0.8, changefreq: "monthly" },
    updated: "2026-10-07",
    breadcrumb: "Spy on competitor ads",
    parent: "/guides",
    ogType: "article",
  },
  {
    path: "/guides/meta-ad-sizes",
    title: "Meta Ad Sizes and Specs: Facebook & Instagram | Mockingbird",
    description:
      "Current Meta ad sizes: 1440 x 1800 for feed, 1440 x 2560 for Stories and Reels, safe zones, text lengths and file limits. Checked against Meta's Ads Guide.",
    indexable: true,
    sitemap: { priority: 0.8, changefreq: "monthly" },
    updated: "2026-10-13",
    breadcrumb: "Meta ad sizes and specs",
    parent: "/guides",
    ogType: "article",
  },
  {
    path: "/guides/test-ad-creative",
    title: "How to Test Ad Creative on Meta | Mockingbird",
    description:
      "How to test Facebook and Instagram ad creative: A/B tests versus several ads in one ad set, what to test first, and which metrics decide the winner.",
    indexable: true,
    sitemap: { priority: 0.8, changefreq: "monthly" },
    updated: "2026-10-07",
    breadcrumb: "How to test ad creative",
    parent: "/guides",
    ogType: "article",
  },
  {
    path: "/blog",
    title: "Meta Advertising News, Explained | Mockingbird Blog",
    description:
      "What changed in Meta advertising, what it means if you run your own ads, and what to do about it. Every post links the sources it was written from.",
    indexable: true,
    sitemap: { priority: 0.7, changefreq: "weekly" },
    updated: "2026-10-09",
    breadcrumb: "Blog",
  },
  {
    path: "/blog/advantage-plus-creative-enhancements",
    title: "Advantage+ Creative Enhancements: Turn Off | Mockingbird",
    description:
      "What Meta's Advantage+ creative enhancements can change in your ad, which are on by default, and how to review and switch each one off in Ads Manager.",
    indexable: true,
    sitemap: { priority: 0.6, changefreq: "monthly" },
    updated: "2026-10-09",
    published: "2026-10-09",
    breadcrumb: "Advantage+ creative enhancements",
    parent: "/blog",
    ogType: "article",
  },
  {
    path: "/blog/does-meta-ad-library-show-spend",
    title: "Does the Meta Ad Library Show Ad Spend? | Mockingbird",
    description:
      "The Meta Ad Library shows spend ranges only for political and issue ads, and reach only for EU and UK ads. Clicks and conversions are never shown.",
    indexable: true,
    sitemap: { priority: 0.6, changefreq: "monthly" },
    updated: "2026-10-13",
    published: "2026-10-09",
    breadcrumb: "Ad Library spend and reach",
    parent: "/blog",
    ogType: "article",
  },
  {
    path: "/blog/meta-less-personalised-ads-eu",
    title: "Meta's Less Personalised Ads in the EU | Mockingbird",
    description:
      "What Meta's less personalised ads choice for EU users is, the dated history behind it, what data it uses, and what it means for your targeting and creative.",
    indexable: true,
    sitemap: { priority: 0.6, changefreq: "monthly" },
    updated: "2026-10-09",
    published: "2026-10-09",
    breadcrumb: "Less personalised ads in the EU",
    parent: "/blog",
    ogType: "article",
  },
  {
    path: "/blog/meta-eu-political-ads-one-year-on",
    title: "Meta's EU Political Ad Ban, One Year On | Mockingbird",
    description:
      "Meta stopped political and social issue ads in the EU on 6 October 2025. What the EU rule requires, what counts as a social issue, and what to check.",
    indexable: true,
    sitemap: { priority: 0.6, changefreq: "monthly" },
    updated: "2026-10-09",
    published: "2026-10-09",
    breadcrumb: "Meta EU political ads ban",
    parent: "/blog",
    ogType: "article",
  },
  {
    path: "/blog/meta-fully-automated-ads-2026",
    title: "Meta's Fully Automated Ads by 2026: The Facts | Mockingbird",
    description:
      "Where Meta's end-of-2026 automated ads goal came from, what Meta has said on the record, what had shipped by October 2026, and what is still unconfirmed.",
    indexable: true,
    sitemap: { priority: 0.6, changefreq: "monthly" },
    updated: "2026-10-09",
    published: "2026-10-09",
    breadcrumb: "Meta fully automated ads 2026",
    parent: "/blog",
    ogType: "article",
  },
  {
    path: "/blog/meta-scam-ads-what-advertisers-should-know",
    title: "Meta Scam Ads: What Advertisers Should Know | Mockingbird",
    description:
      "Reuters reported Meta projected 10% of 2024 revenue from scam ads; Meta disputes it. What followed, and how to verify, appeal a rejection and report fakes.",
    indexable: true,
    sitemap: { priority: 0.6, changefreq: "monthly" },
    updated: "2026-10-09",
    published: "2026-10-09",
    breadcrumb: "Meta scam ads and advertisers",
    parent: "/blog",
    ogType: "article",
  },
  {
    path: "/blog/google-linkedin-tiktok-ad-libraries",
    title: "Google, LinkedIn and TikTok Ad Libraries | Mockingbird",
    description:
      "How to look up a competitor's ads in the Google Ads Transparency Center, LinkedIn Ad Library and TikTok Commercial Content Library, compared with Meta.",
    indexable: true,
    sitemap: { priority: 0.6, changefreq: "monthly" },
    updated: "2026-10-09",
    published: "2026-10-09",
    breadcrumb: "Google, LinkedIn, TikTok ad libraries",
    parent: "/blog",
    ogType: "article",
  },
  {
    path: "/blog/meta-ads-changes-october-2026",
    title: "Meta Ads Changes in October 2026: Key Dates | Mockingbird",
    description:
      "Meta retired Marketing API v24.0 on 6 October 2026 and removes Messenger Stories, poll ads and three delivery estimate fields on 27 October. What to do.",
    indexable: true,
    sitemap: { priority: 0.6, changefreq: "monthly" },
    updated: "2026-10-09",
    published: "2026-10-09",
    breadcrumb: "Meta ads changes, October 2026",
    parent: "/blog",
    ogType: "article",
  },
  {
    path: "/blog/meta-andromeda-gem-explained",
    title: "Meta's Andromeda and GEM Explained Simply | Mockingbird",
    description:
      "What Meta has published about Andromeda and GEM, what each does in ad delivery, and what Meta has and has not said about how many creatives you need.",
    indexable: true,
    sitemap: { priority: 0.6, changefreq: "monthly" },
    updated: "2026-10-09",
    published: "2026-10-09",
    breadcrumb: "Andromeda and GEM explained",
    parent: "/blog",
    ogType: "article",
  },
  {
    path: "/blog/meta-advertising-week-2026",
    title: "Meta at Advertising Week 2026: What Is Live | Mockingbird",
    description:
      "What Meta announced for advertisers on 6 October 2026: which features are open to all now, which are still in testing, and what a small advertiser should do.",
    indexable: true,
    sitemap: { priority: 0.6, changefreq: "monthly" },
    updated: "2026-10-09",
    published: "2026-10-09",
    breadcrumb: "Meta at Advertising Week 2026",
    parent: "/blog",
    ogType: "article",
  },
  {
    path: "/blog/advantage-plus-campaigns-2026",
    title: "Advantage+ Campaigns in 2026: What You Control | Mockingbird",
    description:
      "What makes a Meta campaign Advantage+ in 2026, when Advantage+ shopping and app campaigns closed, and the audience, placement and budget controls you keep.",
    indexable: true,
    sitemap: { priority: 0.6, changefreq: "monthly" },
    updated: "2026-10-13",
    published: "2026-10-09",
    breadcrumb: "Advantage+ campaigns in 2026",
    parent: "/blog",
    ogType: "article",
  },
  {
    path: "/blog/copying-vs-inspiration-competitor-ads",
    title: "Copying vs Inspiration in Competitor Ads | Mockingbird",
    description:
      "What the US Copyright Office, USPTO, FTC, UK regulators and Meta say about borrowing an idea from a competitor's ad versus copying it. Not legal advice.",
    indexable: true,
    sitemap: { priority: 0.6, changefreq: "monthly" },
    updated: "2026-10-13",
    published: "2026-10-09",
    breadcrumb: "Copying vs inspiration in ads",
    parent: "/blog",
    ogType: "article",
  },
  {
    path: "/blog/how-long-has-a-facebook-ad-been-running",
    title: "How Long Has a Facebook Ad Been Running? | Mockingbird",
    description:
      "Find the start date on any Meta Ad Library listing, learn what the active, inactive and version labels mean, and why a long run is not proof of profit.",
    indexable: true,
    sitemap: { priority: 0.6, changefreq: "monthly" },
    updated: "2026-10-09",
    published: "2026-10-09",
    breadcrumb: "How long an ad has run",
    parent: "/blog",
    ogType: "article",
  },
  {
    path: "/blog/meta-ad-library-api",
    title: "Meta Ad Library API: Access, Limits and Data | Mockingbird",
    description:
      "Who can use the Meta Ad Library API, the access steps Meta lists, which ads it covers, the fields it returns and why most commercial ads are missing.",
    indexable: true,
    sitemap: { priority: 0.6, changefreq: "monthly" },
    updated: "2026-10-09",
    published: "2026-10-09",
    breadcrumb: "Meta Ad Library API",
    parent: "/blog",
    ogType: "article",
  },
  {
    path: "/blog/meta-ads-ai-connectors-mcp",
    title: "Meta Ads AI Connectors: MCP Server and CLI | Mockingbird",
    description:
      "What Meta's ads MCP server and Ads CLI let Claude or ChatGPT read and change in your ad account, how to connect one, and the controls Meta describes.",
    indexable: true,
    sitemap: { priority: 0.6, changefreq: "monthly" },
    updated: "2026-10-09",
    published: "2026-10-09",
    breadcrumb: "Meta ads AI connectors",
    parent: "/blog",
    ogType: "article",
  },
  {
    path: "/blog/meta-ads-attribution-settings",
    title: "Meta Ads Attribution Settings Explained | Mockingbird",
    description:
      "What Meta's click-through, engage-through and view-through windows count, what incremental attribution is, and why Ads Manager differs from Google Analytics.",
    indexable: true,
    sitemap: { priority: 0.6, changefreq: "monthly" },
    updated: "2026-10-13",
    published: "2026-10-09",
    breadcrumb: "Meta ads attribution settings",
    parent: "/blog",
    ogType: "article",
  },
  {
    path: "/blog/meta-ads-learning-phase",
    title: "Meta Ads Learning Phase: What Meta Says | Mockingbird",
    description:
      "What Meta's help pages say about the ads learning phase: about 50 results in a week, which edits restart it, and how to fix Learning limited.",
    indexable: true,
    sitemap: { priority: 0.6, changefreq: "monthly" },
    updated: "2026-10-09",
    published: "2026-10-09",
    breadcrumb: "Meta ads learning phase",
    parent: "/blog",
    ogType: "article",
  },
  {
    path: "/blog/meta-ads-manager-vs-business-suite",
    title: "Meta Ads Manager vs Business Suite, Explained | Mockingbird",
    description:
      "Which Meta tool you need: Ads Manager, Business Suite or a business portfolio. The correct address for each, how to log in, and what to check if it fails.",
    indexable: true,
    sitemap: { priority: 0.6, changefreq: "monthly" },
    updated: "2026-10-13",
    published: "2026-10-09",
    breadcrumb: "Ads Manager vs Business Suite",
    parent: "/blog",
    ogType: "article",
  },
  {
    path: "/blog/meta-q2-2026-results-for-advertisers",
    title: "Meta Q2 2026 Results: What Advertisers Should Know | Mockingbird",
    description:
      "Meta's Q2 2026 results: ad revenue up 27%, impressions up 14%, average price per ad up 12%. What those company-wide figures mean for your own ad costs.",
    indexable: true,
    sitemap: { priority: 0.6, changefreq: "monthly" },
    updated: "2026-10-09",
    published: "2026-10-09",
    breadcrumb: "Meta Q2 2026 results",
    parent: "/blog",
    ogType: "article",
  },
  {
    path: "/blog/threads-ads-2026",
    title: "Ads on Threads in 2026: Where They Stand | Mockingbird",
    description:
      "Threads ads opened to all advertisers in April 2025 and reached every market in 2026. Placement defaults, specs, brand controls and what Meta has not said.",
    indexable: true,
    sitemap: { priority: 0.6, changefreq: "monthly" },
    updated: "2026-10-09",
    published: "2026-10-09",
    breadcrumb: "Ads on Threads in 2026",
    parent: "/blog",
    ogType: "article",
  },
  {
    path: "/blog/facebook-ad-creative-explained",
    title: "Facebook Ad Creative: Parts, Formats, Sizes | Mockingbird",
    description:
      "What ad creative means on Facebook and Instagram: the parts, formats, sizes in pixels, Meta's own best practices, and how to preview or share your ad.",
    indexable: true,
    sitemap: { priority: 0.6, changefreq: "monthly" },
    updated: "2026-10-13",
    published: "2026-10-13",
    breadcrumb: "Facebook ad creative explained",
    parent: "/blog",
    ogType: "article",
  },
  {
    path: "/blog/facebook-ads-vs-boosted-posts",
    title: "Facebook Ads vs Boosted Posts: The Difference | Mockingbird",
    description:
      "Facebook ads vs boosted posts, from Meta's own help pages: what a boost lets you set, what Ads Manager adds, how review and billing work, and who each suits.",
    indexable: true,
    sitemap: { priority: 0.6, changefreq: "monthly" },
    updated: "2026-10-13",
    published: "2026-10-13",
    breadcrumb: "Facebook ads vs boosted posts",
    parent: "/blog",
    ogType: "article",
  },
  {
    path: "/blog/facebook-ads-wrong-language-or-location",
    title: "Why Are My Facebook Ads in Another Language? | Mockingbird",
    description:
      "Facebook ads in Spanish, French or Arabic, or showing in other states? The Meta settings that translate ads and widen locations, and how to turn them off.",
    indexable: true,
    sitemap: { priority: 0.6, changefreq: "monthly" },
    updated: "2026-10-13",
    published: "2026-10-13",
    breadcrumb: "Ads in the wrong language",
    parent: "/blog",
    ogType: "article",
  },
  {
    path: "/blog/how-facebook-ad-campaigns-work",
    title: "How Facebook Ad Campaigns Work: Campaign vs Ad Set | Mockingbird",
    description:
      "How a Facebook (Meta) ad campaign works: what is decided at campaign, ad set and ad level, and where the budget sits, taken from Meta's own help pages.",
    indexable: true,
    sitemap: { priority: 0.6, changefreq: "monthly" },
    updated: "2026-10-13",
    published: "2026-10-13",
    breadcrumb: "How Facebook ad campaigns work",
    parent: "/blog",
    ogType: "article",
  },
  {
    path: "/blog/how-much-do-facebook-ads-cost",
    title: "How Much Do Facebook Ads Cost? Meta's Answer | Mockingbird",
    description:
      "Meta has no price list for Facebook and Instagram ads. How budgets, minimums, billing, GST and VAT work on Meta's own pages, and whether Ads Manager is free.",
    indexable: true,
    sitemap: { priority: 0.6, changefreq: "monthly" },
    updated: "2026-10-13",
    published: "2026-10-13",
    breadcrumb: "How much Facebook ads cost",
    parent: "/blog",
    ogType: "article",
  },
  {
    path: "/blog/is-meta-ads-manager-down",
    title: "Is Meta Ads Manager Down? How to Check | Mockingbird",
    description:
      "Is Meta Ads Manager, the Ad Library or Business Suite down? Check Meta's own status page first, then try Meta's browser fixes if the problem is on your side.",
    indexable: true,
    sitemap: { priority: 0.6, changefreq: "monthly" },
    updated: "2026-10-13",
    published: "2026-10-13",
    breadcrumb: "Is Meta Ads Manager down?",
    parent: "/blog",
    ogType: "article",
  },
  {
    path: "/blog/meta-ad-campaign-not-spending",
    title: "Meta Ad Campaign Not Spending? What to Check | Mockingbird",
    description:
      "Why a Meta or Facebook ad campaign is active but not spending or delivering: each Delivery status, the cause Meta gives for it, and the fix, from Meta's pages.",
    indexable: true,
    sitemap: { priority: 0.6, changefreq: "monthly" },
    updated: "2026-10-13",
    published: "2026-10-13",
    breadcrumb: "Meta ad campaign not spending",
    parent: "/blog",
    ogType: "article",
  },
  {
    path: "/blog/meta-ads-campaign-objectives",
    title: "Meta Ads Campaign Objectives: How to Choose | Mockingbird",
    description:
      "Meta's six campaign objectives, how Meta says to choose one, what each limits, and what you need to create a first Facebook or Instagram ad campaign.",
    indexable: true,
    sitemap: { priority: 0.6, changefreq: "monthly" },
    updated: "2026-10-13",
    published: "2026-10-13",
    breadcrumb: "Meta ads campaign objectives",
    parent: "/blog",
    ogType: "article",
  },
  {
    path: "/blog/meta-pixel-explained",
    title: "Meta Pixel Explained: Cost, Cookies and Fixes | Mockingbird",
    description:
      "What the Meta pixel is, whether it is free, how it uses cookies, how it differs from the Conversions API, and what to check when it is not working.",
    indexable: true,
    sitemap: { priority: 0.6, changefreq: "monthly" },
    updated: "2026-10-13",
    published: "2026-10-13",
    breadcrumb: "Meta pixel explained",
    parent: "/blog",
    ogType: "article",
  },
  {
    path: "/blog/pause-stop-delete-meta-ad-campaign",
    title: "How to Pause, Stop or Delete Meta Ad Campaigns | Mockingbird",
    description:
      "How to pause, stop, delete or extend a Meta (Facebook) ad campaign in Ads Manager, and what each does to spend, billing, results and the learning phase.",
    indexable: true,
    sitemap: { priority: 0.6, changefreq: "monthly" },
    updated: "2026-10-13",
    published: "2026-10-13",
    breadcrumb: "Pause, stop or delete a campaign",
    parent: "/blog",
    ogType: "article",
  },
  {
    path: "/blog/advantage-plus-audience-explained",
    title: "Advantage+ Audience Explained: How to Turn It Off | Mockingbird",
    description:
      "What Advantage+ audience is, what Meta does with the targeting you enter, which inputs are hard limits, and how to switch to original audience options.",
    indexable: true,
    sitemap: { priority: 0.6, changefreq: "monthly" },
    updated: "2026-10-13",
    published: "2026-10-13",
    breadcrumb: "Advantage+ audience explained",
    parent: "/blog",
    ogType: "article",
  },
  {
    path: "/blog/advantage-plus-placements-vs-manual",
    title: "Advantage+ Placements vs Manual Placements on Meta | Mockingbird",
    description:
      "Advantage+ placements vs manual placements on Meta: the current placement list, what excluding one changes, the 5% limited spend option and account controls.",
    indexable: true,
    sitemap: { priority: 0.6, changefreq: "monthly" },
    updated: "2026-10-13",
    published: "2026-10-13",
    breadcrumb: "Advantage+ vs manual placements",
    parent: "/blog",
    ogType: "article",
  },
  {
    path: "/blog/create-meta-ad-account",
    title: "How to Create a Meta Ads Manager Account: Steps | Mockingbird",
    description:
      "Every Facebook account already has an ad account. How to open Meta Ads Manager, add more ad accounts, and what a Facebook ad agency account really is.",
    indexable: true,
    sitemap: { priority: 0.6, changefreq: "monthly" },
    updated: "2026-10-13",
    published: "2026-10-13",
    breadcrumb: "Create a Meta ad account",
    parent: "/blog",
    ogType: "article",
  },
  {
    path: "/blog/facebook-ads-for-real-estate",
    title: "Facebook Ads for Real Estate: Housing Rules | Mockingbird",
    description:
      "Meta's housing Special Ad Category rules for real estate ads: who must declare, what age, gender, postcode and lookalike targeting is limited, and what's open.",
    indexable: true,
    sitemap: { priority: 0.6, changefreq: "monthly" },
    updated: "2026-10-13",
    published: "2026-10-13",
    breadcrumb: "Facebook ads for real estate",
    parent: "/blog",
    ogType: "article",
  },
  {
    path: "/blog/how-to-run-instagram-ads",
    title: "How to Run Instagram Ads: Tools, Setup, Fees | Mockingbird",
    description:
      "How to run Instagram ads through the app, instagram.com, Business Suite or Ads Manager: what you need, the no-Facebook route and the Apple fee on iPhone.",
    indexable: true,
    sitemap: { priority: 0.6, changefreq: "monthly" },
    updated: "2026-10-13",
    published: "2026-10-13",
    breadcrumb: "How to run Instagram ads",
    parent: "/blog",
    ogType: "article",
  },
  {
    path: "/blog/meta-ads-bid-strategies",
    title: "Meta Ads Bid Strategies and Campaign Budget | Mockingbird",
    description:
      "The five Meta ads bid strategies, what each tells the delivery system, when Meta suggests each, and how Advantage+ campaign budget works with them.",
    indexable: true,
    sitemap: { priority: 0.6, changefreq: "monthly" },
    updated: "2026-10-13",
    published: "2026-10-13",
    breadcrumb: "Meta ads bid strategies",
    parent: "/blog",
    ogType: "article",
  },
  {
    path: "/blog/meta-ads-billing-and-payments",
    title: "Meta Ads Billing: How Facebook Ads Charge You | Mockingbird",
    description:
      "How Meta bills for Facebook ads: payment thresholds, bill dates and prepaid funds, why a payment fails and the fix, where receipts are, and how ad credits work.",
    indexable: true,
    sitemap: { priority: 0.6, changefreq: "monthly" },
    updated: "2026-10-13",
    published: "2026-10-13",
    breadcrumb: "Meta ads billing and payments",
    parent: "/blog",
    ogType: "article",
  },
  {
    path: "/blog/meta-ads-metrics-explained",
    title: "Meta Ads Metrics Explained: Reach, CTR, CPM, ROAS | Mockingbird",
    description:
      "What reach, impressions, frequency, results, CTR, CPC, CPM, ROAS and hook rate mean in Meta Ads Manager, with the definition and formula Meta gives for each.",
    indexable: true,
    sitemap: { priority: 0.6, changefreq: "monthly" },
    updated: "2026-10-13",
    published: "2026-10-13",
    breadcrumb: "Meta ads metrics explained",
    parent: "/blog",
    ogType: "article",
  },
  {
    path: "/blog/reels-ads-facebook-instagram",
    title: "Ads on Reels: Facebook and Instagram Specs | Mockingbird",
    description:
      "Where Facebook and Instagram ads on Reels appear, the video, image and carousel specs, safe zones, how to select or exclude Reels, and what Meta claims.",
    indexable: true,
    sitemap: { priority: 0.6, changefreq: "monthly" },
    updated: "2026-10-13",
    published: "2026-10-13",
    breadcrumb: "Ads on Facebook and Instagram Reels",
    parent: "/blog",
    ogType: "article",
  },
  {
    path: "/blog/what-is-meta-business-suite",
    title: "What Is Meta Business Suite? Cost, Uses and Fixes | Mockingbird",
    description:
      "What Meta Business Suite is, whether it is free, whether it is a CRM, what you can do in it and Meta's own fixes when it will not post to Instagram.",
    indexable: true,
    sitemap: { priority: 0.6, changefreq: "monthly" },
    updated: "2026-10-13",
    published: "2026-10-13",
    breadcrumb: "What is Meta Business Suite",
    parent: "/blog",
    ogType: "article",
  },
  {
    path: "/blog/advantage-plus-catalogue-ads",
    title: "Advantage+ Catalogue Ads: What They Are Now | Mockingbird",
    description:
      "Advantage+ catalogue ads, formerly dynamic ads: what a catalogue and product set are, what Meta requires, and how they differ from Advantage+ sales campaigns.",
    indexable: true,
    sitemap: { priority: 0.6, changefreq: "monthly" },
    updated: "2026-10-13",
    published: "2026-10-13",
    breadcrumb: "Advantage+ catalogue ads explained",
    parent: "/blog",
    ogType: "article",
  },
  {
    path: "/blog/download-meta-ad-library-videos",
    title: "Can You Download Meta Ad Library Videos? | Mockingbird",
    description:
      "The Meta Ad Library has no download button. What Meta offers instead, what its terms say about downloader tools, and how to study an ad without the file.",
    indexable: true,
    sitemap: { priority: 0.6, changefreq: "monthly" },
    updated: "2026-10-13",
    published: "2026-10-13",
    breadcrumb: "Downloading Ad Library videos",
    parent: "/blog",
    ogType: "article",
  },
  {
    path: "/blog/facebook-ad-library-product-research",
    title: "Facebook Ad Library for Product Research | Mockingbird",
    description:
      "How to use the Facebook Ad Library for product research and dropshipping: keyword and exact-phrase search, filters, what a listing shows, and its limits.",
    indexable: true,
    sitemap: { priority: 0.6, changefreq: "monthly" },
    updated: "2026-10-13",
    published: "2026-10-13",
    breadcrumb: "Ad Library product research",
    parent: "/blog",
    ogType: "article",
  },
  {
    path: "/blog/facebook-ad-text-limits",
    title: "Facebook Ad Text Limits: Headline and Primary Text | Mockingbird",
    description:
      "Facebook and Instagram ad text explained: what primary text, headline and description are, and the character lengths Meta recommends for each placement.",
    indexable: true,
    sitemap: { priority: 0.6, changefreq: "monthly" },
    updated: "2026-10-13",
    published: "2026-10-13",
    breadcrumb: "Facebook ad text limits",
    parent: "/blog",
    ogType: "article",
  },
  {
    path: "/blog/facebook-ads-targeting-options",
    title: "Facebook Ads Targeting Options and Limits | Mockingbird",
    description:
      "What Meta lets you target on Facebook and Instagram ads: location, postcodes, age, interests, custom and lookalike audiences, and what has been removed.",
    indexable: true,
    sitemap: { priority: 0.6, changefreq: "monthly" },
    updated: "2026-10-13",
    published: "2026-10-13",
    breadcrumb: "Facebook ads targeting options",
    parent: "/blog",
    ogType: "article",
  },
  {
    path: "/blog/install-meta-pixel-shopify-wordpress",
    title: "Install Meta Pixel on Shopify and WordPress | Mockingbird",
    description:
      "The official way to add the Meta pixel to Shopify and WordPress, what each integration sets up, and the fixes each help page gives when it will not connect.",
    indexable: true,
    sitemap: { priority: 0.6, changefreq: "monthly" },
    updated: "2026-10-13",
    published: "2026-10-13",
    breadcrumb: "Meta pixel on Shopify and WordPress",
    parent: "/blog",
    ogType: "article",
  },
  {
    path: "/blog/meta-ad-library-filters-explained",
    title: "Meta Ad Library Filters and Labels Explained | Mockingbird",
    description:
      "Every Meta Ad Library filter and sort option, what the low impression count label means, and why commercial ads cannot be sorted by performance.",
    indexable: true,
    sitemap: { priority: 0.6, changefreq: "monthly" },
    updated: "2026-10-13",
    published: "2026-10-13",
    breadcrumb: "Ad Library filters explained",
    parent: "/blog",
    ogType: "article",
  },
  {
    path: "/blog/meta-ads-certification-blueprint",
    title: "Meta Ads Certification: Costs and Free Courses | Mockingbird",
    description:
      "Meta's current ads certifications, what each exam costs, how it is taken and renewed, the free Meta Blueprint courses, and how to judge any other course.",
    indexable: true,
    sitemap: { priority: 0.6, changefreq: "monthly" },
    updated: "2026-10-13",
    published: "2026-10-13",
    breadcrumb: "Meta ads certification",
    parent: "/blog",
    ogType: "article",
  },
  {
    path: "/blog/meta-ads-for-nonprofits",
    title: "Meta Ads for Nonprofits: What Is Different | Mockingbird",
    description:
      "Meta ads for nonprofits, from Meta's own pages: no ad credits or grants, where the donation tools work, and when a charity's ad needs a Paid for by label.",
    indexable: true,
    sitemap: { priority: 0.6, changefreq: "monthly" },
    updated: "2026-10-13",
    published: "2026-10-13",
    breadcrumb: "Meta ads for nonprofits",
    parent: "/blog",
    ogType: "article",
  },
  {
    path: "/blog/meta-lead-ads-instant-forms",
    title: "Meta Lead Ads and Instant Forms Explained | Mockingbird",
    description:
      "What Facebook lead ads and instant forms are, from Meta's own help pages: form types, questions, the 90-day download limit, performance goals and Advantage+.",
    indexable: true,
    sitemap: { priority: 0.6, changefreq: "monthly" },
    updated: "2026-10-13",
    published: "2026-10-13",
    breadcrumb: "Meta lead ads and instant forms",
    parent: "/blog",
    ogType: "article",
  },
  {
    path: "/case-studies/nexlytics",
    title: "Nexlytics Case Study | Competitor Ads Rebuilt by Mockingbird",
    description:
      "How Nexlytics used Mockingbird to turn competitor ads from Monte Carlo and HubSpot into on-brand Meta ad variants. Real source ads and real output, side by side.",
    indexable: true,
    sitemap: { priority: 0.7, changefreq: "monthly" },
    updated: "2026-10-07",
    breadcrumb: "Nexlytics case study",
    ogType: "article",
  },
  {
    path: "/security",
    title: "Security & Data Handling | Mockingbird",
    description:
      "What Mockingbird stores, where it lives, and who can see it: workspace isolation, private storage, Meta token handling, and how to delete your data.",
    indexable: true,
    sitemap: { priority: 0.5, changefreq: "monthly" },
    updated: "2026-10-07",
    breadcrumb: "Security",
  },
  {
    // The paid-traffic sales page. Indexable with its own canonical, but kept
    // out of the sitemap: it is a time-boxed offer, not an evergreen page.
    path: "/lifetime",
    title: "Mockingbird Lifetime Deal | Competitor Ads Rebuilt for Your Brand",
    description:
      "Paste a competitor. Get their best ad, rebuilt for your brand. One payment for lifetime access to Mockingbird's competitor ad research and AI ad generation.",
    indexable: true,
    updated: "2026-10-07",
    breadcrumb: "Lifetime deal",
  },
  {
    path: "/signup",
    title: "Start Your 7-Day Trial | Mockingbird",
    description: `Create a Mockingbird account and start a 7-day trial for $${TRIAL_PRICE_USD}. Add a competitor and get their best Meta ads rebuilt for your brand.`,
    indexable: true,
    updated: "2026-10-07",
    breadcrumb: "Sign up",
    headOnly: true,
  },
  {
    path: "/login",
    title: "Log In | Mockingbird",
    description: "Log in to your Mockingbird workspace to see today's competitor ad drop and your generated Meta ad variants.",
    indexable: true,
    updated: "2026-10-07",
    breadcrumb: "Log in",
    headOnly: true,
  },
  {
    path: "/legal/terms",
    title: "Terms of Service | Mockingbird",
    description: "The terms that govern use of Mockingbird, the competitor ad research and AI ad generation service from Onwards Analytics.",
    indexable: true,
    sitemap: { priority: 0.2, changefreq: "yearly" },
    updated: "2026-09-22",
    breadcrumb: "Terms",
  },
  {
    path: "/legal/privacy",
    title: "Privacy Policy | Mockingbird",
    description: "What personal data Mockingbird collects, why, who processes it, and how to access or delete it.",
    indexable: true,
    sitemap: { priority: 0.2, changefreq: "yearly" },
    updated: "2026-10-02",
    breadcrumb: "Privacy",
  },
];

/**
 * Routes that exist only in the browser: everything behind a login, and the
 * one-off flows (auth callback, invitation, thank-you) whose content depends
 * on the query string. Cloudflare Pages serves the empty app shell for these.
 *
 * A trailing "/*" matches the path and everything under it. Every <Route> in
 * src/app.tsx must be either a PUBLIC_PAGES path or covered here;
 * src/lib/__tests__/seo.test.ts fails the build when one is not, because the
 * symptom otherwise is a hard refresh on that route answering 404.
 */
export const APP_SHELL_ROUTES: readonly string[] = [
  "/reset-password",
  "/auth/callback",
  "/invitations/accept",
  "/lifetime/thank-you",
  "/lifetime/upgrade",
  "/onboarding",
  "/dashboard",
  "/create-ad",
  "/meta-analytics",
  "/brand",
  "/account",
  "/runs/*",
  "/cmo",
  "/settings/*",
];

/**
 * Blog posts worth reading next from a guide or product page. A post is only
 * linked from the /blog hub unless something points at it, and a page with one
 * inbound link is a page search engines treat as unimportant. Keys are pages;
 * values are blog slugs, most relevant first.
 */
const RELATED_POSTS: Record<string, readonly string[]> = {
  "/competitor-ad-research": ["how-long-has-a-facebook-ad-been-running", "does-meta-ad-library-show-spend", "google-linkedin-tiktok-ad-libraries"],
  "/ai-ad-generator": ["copying-vs-inspiration-competitor-ads", "meta-fully-automated-ads-2026", "advantage-plus-creative-enhancements"],
  "/meta-ad-library-alternative": ["does-meta-ad-library-show-spend", "meta-ad-library-api", "how-long-has-a-facebook-ad-been-running"],
  "/guides/meta-ad-library": ["does-meta-ad-library-show-spend", "how-long-has-a-facebook-ad-been-running", "meta-ad-library-api"],
  "/guides/find-competitor-ads-instagram": ["how-long-has-a-facebook-ad-been-running", "google-linkedin-tiktok-ad-libraries", "threads-ads-2026"],
  "/guides/spy-on-competitor-ads": ["google-linkedin-tiktok-ad-libraries", "copying-vs-inspiration-competitor-ads", "meta-eu-political-ads-one-year-on"],
  "/guides/meta-ad-sizes": ["threads-ads-2026", "advantage-plus-creative-enhancements", "meta-ads-changes-october-2026"],
  "/guides/how-to-publish-meta-ads": ["meta-ads-manager-vs-business-suite", "advantage-plus-campaigns-2026", "meta-scam-ads-what-advertisers-should-know"],
  "/guides/test-ad-creative": ["meta-ads-learning-phase", "meta-ads-attribution-settings", "meta-andromeda-gem-explained"],
  "/guides/meta-ad-ranking": ["meta-andromeda-gem-explained", "meta-ads-learning-phase", "meta-q2-2026-results-for-advertisers"],
};

/**
 * Posts to list under "From the blog" on a page: the hand-picked ones above,
 * or, on a post itself, the next three posts in PUBLIC_PAGES order (wrapping),
 * so every post is linked from three others without anyone maintaining a list.
 */
export function relatedPostsFor(path: string): SeoPage[] {
  const posts = PUBLIC_PAGES.filter((page) => page.parent === "/blog");
  const picked = RELATED_POSTS[path];
  if (picked) {
    return picked
      .map((slug) => posts.find((post) => post.path === `/blog/${slug}`))
      .filter((post): post is SeoPage => Boolean(post));
  }
  const index = posts.findIndex((post) => post.path === path);
  if (index < 0) return [];
  return [1, 2, 3].map((step) => posts[(index + step) % posts.length]!).filter((post) => post.path !== path);
}

/** Head tags for anything that is not a public page: the shell and the 404. */
export const SHELL_SEO: SeoPage = {
  path: "",
  title: SITE_NAME,
  description: "",
  indexable: false,
  updated: "2026-10-07",
  breadcrumb: SITE_NAME,
};

export const NOT_FOUND_SEO: SeoPage = {
  ...SHELL_SEO,
  title: `Page not found | ${SITE_NAME}`,
};

/** "/pricing/" and "/pricing" are the same page; "/" stays "/". */
export function normalisePath(pathname: string): string {
  const trimmed = pathname.replace(/\/+$/, "");
  return trimmed === "" ? "/" : trimmed;
}

export function seoForPath(pathname: string): SeoPage | undefined {
  const path = normalisePath(pathname);
  return PUBLIC_PAGES.find((page) => page.path === path);
}

export function isAppShellPath(pathname: string): boolean {
  const path = normalisePath(pathname);
  return APP_SHELL_ROUTES.some((route) =>
    route.endsWith("/*")
      ? path === route.slice(0, -2) || path.startsWith(route.slice(0, -1))
      : path === route,
  );
}

export function canonicalUrl(page: SeoPage): string {
  return page.path === "/" ? `${SITE_ORIGIN}/` : `${SITE_ORIGIN}${page.path}`;
}

// ---------------------------------------------------------------------------
// Structured data
// ---------------------------------------------------------------------------

type JsonLd = Record<string, unknown>;

const ORGANIZATION_ID = `${SITE_ORIGIN}/#organization`;
const WEBSITE_ID = `${SITE_ORIGIN}/#website`;
const SOFTWARE_ID = `${SITE_ORIGIN}/#software`;

/** One film: what the player, the transcript and the VideoObject all read. */
export interface SiteVideo {
  name: string;
  description: string;
  src: string;
  poster: string;
  width: number;
  height: number;
  /** ISO 8601 duration. */
  duration: string;
  uploadDate: string;
  /** Short line under the player. */
  caption: string;
  /** The narration, word for word, one spoken line per entry. */
  transcript: readonly string[];
  /**
   * A 9:16 cut of the same film. When present, phone-width screens get this
   * one and everything wider gets the landscape cut.
   */
  vertical?: { src: string; poster: string; width: number; height: number };
}

/**
 * The product films shown on public pages.
 *
 * Each entry feeds three things: the <video> element, the transcript printed
 * under it, and the VideoObject JSON-LD that lets a search engine list the
 * video. The transcript is the narration word for word, so a crawler and a
 * visitor with the sound off get what a listener gets.
 *
 * Before adding a film, read its narration and look at its frames. Most cuts
 * in the Remotion project say ads are "sent to Meta, paused" over a Push to
 * Meta panel, which the product does not do today (see META_PUSH_ENABLED),
 * and several end on the lifetime price. Neither belongs on these pages; a
 * test checks every transcript here for both.
 *
 * The files are 1280 x 720 web copies of the 1080p masters, a quarter of the
 * size and sharper than the player ever shows them. A film can also carry a
 * 720 x 1280 vertical cut for phones.
 */
export const VIDEOS = {
  itsPublic: {
    name: "Mockingbird: your competitor's best ad, rebuilt for your brand",
    description:
      "A 33-second introduction to Mockingbird. It finds the competitor ads that have been running longest in Meta's Ad Library and rebuilds the idea in your brand's voice, colours and offer.",
    src: "/videos/mockingbird-its-public.mp4",
    poster: "/videos/mockingbird-its-public-poster.jpg",
    width: 1280,
    height: 720,
    duration: "PT33S",
    uploadDate: "2026-10-07",
    caption: "Mockingbird in 33 seconds.",
    transcript: [
      "Your competitor's best ad? It's public. Right now!",
      "It's sitting in Meta's ad library, for anyone to see!",
      "Mockingbird finds the ads that have been running the longest!",
      "Then it rebuilds the idea for your brand. Your voice! Your colours! Your offer!",
      "You get on-brand variants, ready to edit and export!",
      "No copying. No guessing.",
      "Mockingbird! Their best idea, your brand.",
    ],
  },
  quickQuestions: {
    name: "Quick questions about Mockingbird",
    description:
      "Seven questions about Mockingbird answered in a minute: whether it is just an ad generator, where the competitor ads come from, whether the ads sound like your brand, editing, Ads Manager, and who it is for.",
    src: "/videos/mockingbird-quick-questions.mp4",
    poster: "/videos/mockingbird-quick-questions-poster.jpg",
    width: 1280,
    height: 720,
    duration: "PT61S",
    uploadDate: "2026-10-07",
    caption: "Seven quick questions, answered in a minute.",
    transcript: [
      "Quick questions about Mockingbird. Go!",
      "Is it just another AI ad generator?",
      "Nope! It watches competitors, learns your taste, builds the test, and reads your Meta results.",
      "Where do the competitor ads come from?",
      "Meta's public ad library. Only public ads!",
      "Will the ads actually sound like us?",
      "Yes! Your voice goes into every prompt, and blocked words get rejected.",
      "Can I edit what it makes?",
      "Yes! In editable mode the text stays live, and you export when you're happy.",
      "Does it replace Ads Manager?",
      "Nope! You download each ad and launch it yourself. Budgets and final launches stay in your ad account.",
      "And does it get smarter?",
      "That's the whole idea! Every result feeds the next recommendation.",
      "Who's it for?",
      "Founders, performance marketers and agencies!",
      "Mockingbird! Your next ad test, sorted.",
    ],
  },
  fiveSteps: {
    name: "How Mockingbird works, in five steps",
    description:
      "A one-minute walkthrough of Mockingbird in five steps: reading competitors' public Meta ads, saving your brand's voice, generating editable ad tests, downloading them to launch yourself, and learning from the results.",
    src: "/videos/mockingbird-five-steps.mp4",
    poster: "/videos/mockingbird-five-steps-poster.jpg",
    width: 1280,
    height: 720,
    duration: "PT61S",
    uploadDate: "2026-10-08",
    caption: "How Mockingbird works, in five steps and one minute.",
    transcript: [
      "Here's how Mockingbird works, in five steps.",
      "Step one: connect the market. Mockingbird reads your competitors' public Meta ads.",
      "And ranks them into a short daily drop.",
      "Step two: define the brand. Save your voice, your palette, and the words to avoid.",
      "Your voice goes into every prompt.",
      "And anything off-brand gets rejected.",
      "Step three: generate tests. One click turns the CMO's brief into ads.",
      "Each one stays editable, so you can tweak the words.",
      "Step four: export. You download each ad, and launch it yourself in Ads Manager.",
      "And every costly action shows its price in credits before you run it.",
      "Step five: learn from results. Meta performance flows back into the CMO Brain.",
      "Then the loop starts again: it watches, learns, builds and reads.",
      "Mockingbird. From market signal to your next test.",
    ],
    vertical: {
      src: "/videos/mockingbird-five-steps-vertical.mp4",
      poster: "/videos/mockingbird-five-steps-vertical-poster.jpg",
      width: 720,
      height: 1280,
    },
  },
} as const satisfies Record<string, SiteVideo>;

export type VideoKey = keyof typeof VIDEOS;

/**
 * Which film each page shows. A page listed here carries that film's
 * VideoObject, and its route must render <ProductVideo video="…" /> with the
 * same key (a test checks).
 */
export const PAGE_VIDEO: Readonly<Record<string, VideoKey>> = {
  "/": "fiveSteps",
  "/how-it-works": "quickQuestions",
  "/competitor-ad-research": "itsPublic",
};

function videoLd(video: SiteVideo): JsonLd {
  return {
    "@type": "VideoObject",
    name: video.name,
    description: video.description,
    thumbnailUrl: `${SITE_ORIGIN}${video.poster}`,
    contentUrl: `${SITE_ORIGIN}${video.src}`,
    uploadDate: video.uploadDate,
    duration: video.duration,
    transcript: video.transcript.join(" "),
    publisher: { "@id": ORGANIZATION_ID },
    inLanguage: "en",
  };
}

/**
 * Mockingbird's own profiles on other sites, as `sameAs` on the Organization.
 * This is how a search engine learns that the LinkedIn page called
 * "Mockingbird" is this Mockingbird and not one of the two hundred others.
 *
 * Add a URL here only once the profile is live and ours. The footer links to
 * the first entry of each network, so a wrong URL is also a visible one.
 */
export const SOCIAL_PROFILES = {
  linkedin: "https://www.linkedin.com/company/mockingbirddigital",
  youtube: "https://www.youtube.com/@mockingbird.digital",
} as const;

/**
 * The brand icon as a 512 px PNG: the same bird the favicon and the social
 * profiles use. Search engines want a raster logo of at least 112 px.
 */
export const ORGANIZATION_LOGO = "/icon-512.png";

function organizationLd(): JsonLd {
  return {
    "@type": "Organization",
    "@id": ORGANIZATION_ID,
    name: SITE_NAME,
    // Other ad products are also called Mockingbird. The publisher's name and
    // the domain are what tell this one apart, so both are spelled out.
    alternateName: [`${SITE_NAME} by ${PUBLISHER_NAME}`, "mockingbird.digital"],
    url: `${SITE_ORIGIN}/`,
    logo: `${SITE_ORIGIN}${ORGANIZATION_LOGO}`,
    description:
      "Mockingbird is a competitor ad research and AI ad generation tool for Meta advertisers.",
    disambiguatingDescription: `The Mockingbird made by ${PUBLISHER_NAME} in Perth, Australia. Its only website is mockingbird.digital; it is not connected to other products named Mockingbird.`,
    foundingDate: "2026",
    address: {
      "@type": "PostalAddress",
      addressLocality: "Perth",
      addressRegion: "WA",
      addressCountry: "AU",
    },
    sameAs: Object.values(SOCIAL_PROFILES),
    parentOrganization: { "@type": "Organization", name: PUBLISHER_NAME, url: PUBLISHER_URL },
  };
}

function websiteLd(): JsonLd {
  return {
    "@type": "WebSite",
    "@id": WEBSITE_ID,
    name: SITE_NAME,
    alternateName: [`${SITE_NAME} by ${PUBLISHER_NAME}`, "Mockingbird Ads", "Mockingbird AI", "mockingbird.digital"],
    url: `${SITE_ORIGIN}/`,
    publisher: { "@id": ORGANIZATION_ID },
    inLanguage: "en",
  };
}

function softwareLd(): JsonLd {
  const paidPlans = [
    PLAN_CREDIT_POLICIES.basic,
    PLAN_CREDIT_POLICIES.pro,
    PLAN_CREDIT_POLICIES.pro_max,
  ].map((plan) => plan.monthlyPriceUsd);
  return {
    "@type": "SoftwareApplication",
    "@id": SOFTWARE_ID,
    name: SITE_NAME,
    url: `${SITE_ORIGIN}/`,
    applicationCategory: "BusinessApplication",
    applicationSubCategory: "Advertising software",
    operatingSystem: "Web",
    description:
      "Mockingbird watches competitor ads in the Meta Ad Library, scores them, delivers the strongest ones to your feed every day, and generates on-brand variants you download and run in Meta Ads Manager.",
    featureList: [
      "Competitor ad monitoring from the Meta Ad Library",
      "Daily feed of scored competitor ads",
      "AI ad generation matched to your brand profile",
      "Export sized for Meta feed and stories",
      "Meta Ads performance analysis",
      "Creative recommendations that learn from your reactions",
    ],
    offers: {
      "@type": "AggregateOffer",
      priceCurrency: "USD",
      lowPrice: String(Math.min(TRIAL_PRICE_USD, ...paidPlans)),
      highPrice: String(Math.max(...paidPlans)),
      offerCount: paidPlans.length + 1,
      url: `${SITE_ORIGIN}/pricing`,
    },
    publisher: { "@id": ORGANIZATION_ID },
  };
}

/** Home, then the parent (if any), then the page. Also drives the visible
 *  breadcrumb trail, so the markup and the page cannot disagree. */
export function breadcrumbTrail(page: SeoPage): Array<{ name: string; path: string }> {
  const trail = [{ name: "Home", path: "/" }];
  if (page.path === "/") return trail;
  const parent = page.parent ? PUBLIC_PAGES.find((p) => p.path === page.parent) : undefined;
  if (parent) trail.push({ name: parent.breadcrumb, path: parent.path });
  trail.push({ name: page.breadcrumb, path: page.path });
  return trail;
}

function breadcrumbLd(page: SeoPage): JsonLd | null {
  if (page.path === "/") return null;
  return {
    "@type": "BreadcrumbList",
    itemListElement: breadcrumbTrail(page).map((crumb, index) => ({
      "@type": "ListItem",
      position: index + 1,
      name: crumb.name,
      item: crumb.path === "/" ? `${SITE_ORIGIN}/` : `${SITE_ORIGIN}${crumb.path}`,
    })),
  };
}

/** The questions a page shows, if it has any. Pages render this same list. */
export function faqForPath(pathname: string): Faq {
  return PAGE_FAQ[normalisePath(pathname)] ?? [];
}

function faqLd(faq: Faq): JsonLd {
  return {
    "@type": "FAQPage",
    mainEntity: faq.map(([question, answer]) => ({
      "@type": "Question",
      name: question,
      acceptedAnswer: { "@type": "Answer", text: answer },
    })),
  };
}

/** Pages whose subject is the product itself, so carry its full description. */
const PRODUCT_PAGES: ReadonlySet<string> = new Set([
  "/",
  "/pricing",
  "/competitor-ad-research",
  "/ai-ad-generator",
  "/meta-ad-library-alternative",
  "/examples",
  "/foreplay-alternative",
  "/adspy-alternative",
  "/adcreative-ai-alternative",
  "/gethookd-alternative",
  "/adstellar-alternative",
  "/atria-alternative",
]);

/**
 * One @graph per page. Organization and WebSite go on every public page so
 * each URL carries the entity it belongs to; the product goes on the pages
 * about it, and a FAQ goes wherever lib/faq.ts has one for that path.
 */
export function jsonLdForPage(page: SeoPage): JsonLd | null {
  if (!page.indexable) return null;
  const graph: JsonLd[] = [organizationLd(), websiteLd()];
  graph.push({
    "@type": page.ogType === "article" ? "Article" : "WebPage",
    "@id": `${canonicalUrl(page)}#webpage`,
    url: canonicalUrl(page),
    name: page.title,
    ...(page.ogType === "article" ? { headline: page.title } : {}),
    description: page.description,
    isPartOf: { "@id": WEBSITE_ID },
    about: { "@id": SOFTWARE_ID },
    ...(page.published ? { datePublished: page.published } : {}),
    dateModified: page.updated,
    inLanguage: "en",
    ...(page.ogType === "article"
      ? { author: { "@id": ORGANIZATION_ID }, publisher: { "@id": ORGANIZATION_ID }, image: DEFAULT_OG_IMAGE }
      : {}),
  });
  if (PRODUCT_PAGES.has(page.path)) graph.push(softwareLd());
  const faq = faqForPath(page.path);
  if (faq.length > 0) graph.push(faqLd(faq));
  const videoKey = PAGE_VIDEO[page.path];
  if (videoKey) graph.push(videoLd(VIDEOS[videoKey]));
  const crumbs = breadcrumbLd(page);
  if (crumbs) graph.push(crumbs);
  return { "@context": "https://schema.org", "@graph": graph };
}

// ---------------------------------------------------------------------------
// <head> rendering (build time)
// ---------------------------------------------------------------------------

export function escapeHtml(value: string): string {
  return value
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

/**
 * The block scripts/prerender.mjs puts between the `seo:start` and `seo:end`
 * markers in index.html. JSON-LD is escaped so a "</script>" inside a string
 * cannot close the tag.
 */
export function renderHeadTags(page: SeoPage): string {
  const lines: string[] = [`<title>${escapeHtml(page.title)}</title>`];
  if (!page.indexable) {
    lines.push(`<meta name="robots" content="noindex, nofollow" />`);
    return lines.join("\n    ");
  }
  const url = canonicalUrl(page);
  const title = escapeHtml(page.title);
  const description = escapeHtml(page.description);
  lines.push(
    `<meta name="description" content="${description}" />`,
    `<meta name="robots" content="index, follow, max-image-preview:large, max-snippet:-1" />`,
    `<link rel="canonical" href="${url}" />`,
    `<link rel="alternate" type="application/rss+xml" title="${SITE_NAME} blog" href="${SITE_ORIGIN}${BLOG_FEED_PATH}" />`,
    `<meta property="og:site_name" content="${SITE_NAME}" />`,
    `<meta property="og:type" content="${page.ogType ?? "website"}" />`,
    `<meta property="og:title" content="${title}" />`,
    `<meta property="og:description" content="${description}" />`,
    `<meta property="og:url" content="${url}" />`,
    `<meta property="og:image" content="${DEFAULT_OG_IMAGE}" />`,
    `<meta property="og:image:width" content="1200" />`,
    `<meta property="og:image:height" content="630" />`,
    `<meta property="og:image:alt" content="${escapeHtml(DEFAULT_OG_IMAGE_ALT)}" />`,
    `<meta name="twitter:card" content="summary_large_image" />`,
    `<meta name="twitter:title" content="${title}" />`,
    `<meta name="twitter:description" content="${description}" />`,
    `<meta name="twitter:image" content="${DEFAULT_OG_IMAGE}" />`,
    `<meta name="twitter:image:alt" content="${escapeHtml(DEFAULT_OG_IMAGE_ALT)}" />`,
  );
  const jsonLd = jsonLdForPage(page);
  if (jsonLd) {
    const json = JSON.stringify(jsonLd).replace(/</g, "\\u003c");
    lines.push(`<script type="application/ld+json">${json}</script>`);
  }
  return lines.join("\n    ");
}

// ---------------------------------------------------------------------------
// Generated files (build time)
// ---------------------------------------------------------------------------

export function renderSitemap(): string {
  const urls = PUBLIC_PAGES.filter((page) => page.indexable && page.sitemap).map(
    (page) =>
      [
        "  <url>",
        `    <loc>${canonicalUrl(page)}</loc>`,
        `    <lastmod>${page.updated}</lastmod>`,
        `    <changefreq>${page.sitemap!.changefreq}</changefreq>`,
        `    <priority>${page.sitemap!.priority.toFixed(1)}</priority>`,
        "  </url>",
      ].join("\n"),
  );
  return [
    `<?xml version="1.0" encoding="UTF-8"?>`,
    `<urlset xmlns="http://www.sitemaps.org/schemas/sitemap/0.9">`,
    ...urls,
    `</urlset>`,
    "",
  ].join("\n");
}

/** The blog's RSS feed, relative to the site root. Linked from every page's <head>. */
export const BLOG_FEED_PATH = "/blog/feed.xml";

function escapeXml(value: string): string {
  return escapeHtml(value).replace(/'/g, "&apos;");
}

/**
 * RSS 2.0 for the blog, newest first. Feed readers and several AI crawlers
 * poll a feed far more often than they re-read a sitemap, and it costs one
 * generated file. Dates are noon UTC so no time zone shows the previous day.
 */
export function renderBlogFeed(): string {
  const posts = PUBLIC_PAGES.filter((page) => page.parent === "/blog" && page.indexable).sort((a, b) =>
    (b.published ?? b.updated).localeCompare(a.published ?? a.updated),
  );
  const rfc822 = (iso: string) => new Date(`${iso}T12:00:00Z`).toUTCString();
  const newest = posts[0] ? (posts[0].published ?? posts[0].updated) : "2026-10-09";
  const items = posts.map((post) =>
    [
      "    <item>",
      `      <title>${escapeXml(post.title.replace(/ \| Mockingbird$/, ""))}</title>`,
      `      <link>${canonicalUrl(post)}</link>`,
      `      <guid isPermaLink="true">${canonicalUrl(post)}</guid>`,
      `      <pubDate>${rfc822(post.published ?? post.updated)}</pubDate>`,
      `      <description>${escapeXml(post.description)}</description>`,
      "    </item>",
    ].join("\n"),
  );
  return [
    `<?xml version="1.0" encoding="UTF-8"?>`,
    `<rss version="2.0" xmlns:atom="http://www.w3.org/2005/Atom">`,
    "  <channel>",
    `    <title>${SITE_NAME} blog</title>`,
    `    <link>${SITE_ORIGIN}/blog</link>`,
    `    <atom:link href="${SITE_ORIGIN}${BLOG_FEED_PATH}" rel="self" type="application/rss+xml" />`,
    "    <description>What changed in Meta advertising, what it means if you run your own ads, and what to do about it.</description>",
    "    <language>en</language>",
    `    <lastBuildDate>${rfc822(newest)}</lastBuildDate>`,
    ...items,
    "  </channel>",
    "</rss>",
    "",
  ].join("\n");
}

/** Where a page's HTML is written, relative to dist/. Cloudflare Pages serves
 *  `pricing.html` at `/pricing` with no trailing-slash redirect; a
 *  `pricing/index.html` would 308 `/pricing` to `/pricing/`. */
export function outputFileForPath(path: string): string {
  return path === "/" ? "index.html" : `${path.slice(1)}.html`;
}

export const APP_SHELL_FILE = "app-shell.html";

/**
 * Cloudflare Pages _redirects. Status 200 is a rewrite: the URL stays, the
 * shell is served. The target is extensionless on purpose — Pages treats a
 * rewrite to "/x.html" as an infinite loop (it strips ".html") and silently
 * drops the rule, which is what happened to the old "/* /index.html 200".
 *
 * There is deliberately no catch-all. With a 404.html in the build output,
 * anything not listed here and not a real file answers 404, which is what
 * stops made-up URLs from being indexed as copies of the homepage.
 */
export function renderRedirects(): string {
  const target = `/${APP_SHELL_FILE.replace(/\.html$/, "")}`;
  const lines = [
    "# GENERATED by apps/web/scripts/prerender.mjs from src/lib/seo.ts.",
    "# Do not edit: add the route to APP_SHELL_ROUTES instead.",
  ];
  for (const route of APP_SHELL_ROUTES) {
    if (route.endsWith("/*")) {
      // "/runs/*" matches "/runs/" and below, but not "/runs" itself.
      lines.push(`${route.slice(0, -2)} ${target} 200`);
      lines.push(`${route} ${target} 200`);
    } else {
      // Pages matches exactly: without the second rule "/dashboard/" falls
      // through to 404.html (the app still boots, but with a 404 status).
      lines.push(`${route} ${target} 200`);
      lines.push(`${route}/ ${target} 200`);
    }
  }
  return `${lines.join("\n")}\n`;
}
